import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useResearchItems, useTaxonomy, useTransitionStatus, type ResearchStatus, type ResearchAxis } from "@/hooks/useResearch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const STATUSES: (ResearchStatus | "all")[] = ["all", "draft", "in_review", "approved", "published", "archived", "rejected"];
const AXES: (ResearchAxis | "all")[] = ["all", "asset_class", "geography", "sector", "theme", "instrument_type", "custom"];

const statusColor: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  in_review: "bg-yellow-100 text-yellow-900",
  approved: "bg-green-100 text-green-900",
  published: "bg-blue-100 text-blue-900",
  archived: "bg-gray-200 text-gray-700",
  rejected: "bg-red-100 text-red-900",
};

interface ResearchListProps {
  defaultStatus?: ResearchStatus | "all";
  title?: string;
  description?: string;
}

export default function ResearchList({ defaultStatus = "all", title, description }: ResearchListProps = {}) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<ResearchStatus | "all">(defaultStatus);
  const [axis, setAxis] = useState<ResearchAxis | "all">("all");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data: items, isLoading } = useResearchItems({ q, status, axis, tagIds });
  const { data: taxonomy } = useTaxonomy();
  const transition = useTransitionStatus();

  const visibleIds = useMemo(() => (items ?? []).map((i) => i.id), [items]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id)) && !allSelected;

  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) visibleIds.forEach((id) => next.add(id));
      else visibleIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const applyStatus = async (ids: string[], newStatus: ResearchStatus) => {
    if (ids.length === 0) return;
    let ok = 0, failed = 0;
    for (const id of ids) {
      try { await transition.mutateAsync({ id, status: newStatus }); ok++; }
      catch (e: any) { failed++; console.error(e); }
    }
    if (failed === 0) toast.success(`${ok} item(s) ${newStatus}`);
    else toast.error(`${ok} ${newStatus}, ${failed} failed`);
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const bulkApprove = () => applyStatus(Array.from(selected), "approved");
  const bulkReject = () => applyStatus(Array.from(selected), "rejected");

  return (
    <div className="space-y-4">
      {(title || description) && (
        <div className="space-y-1">
          {title && <h1 className="text-2xl font-semibold">{title}</h1>}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search title..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <Select value={status} onValueChange={(v) => setStatus(v as any)}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={axis} onValueChange={(v) => setAxis(v as any)}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {AXES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button asChild className="ml-auto"><Link to="/partner/research/new">+ New</Link></Button>
      </div>

      {taxonomy && taxonomy.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {taxonomy.slice(0, 30).map((t) => {
            const active = tagIds.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => setTagIds((prev) => active ? prev.filter((x) => x !== t.id) : [...prev, t.id])}
                className={`text-xs px-2 py-1 rounded border ${active ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background text-muted-foreground hover:border-primary"}`}
              >
                {t.axis}: {t.value}
              </button>
            );
          })}
        </div>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {items && items.length === 0 && <p className="text-sm text-muted-foreground">No entries</p>}

      {items && items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-b bg-muted/30">
              <div className="text-xs text-muted-foreground">
                {selected.size > 0 ? `${selected.size} selected` : `${items.length} item(s)`}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={selected.size === 0 || transition.isPending}
                  onClick={bulkApprove}>
                  <Check className="h-3 w-3 mr-1" /> Approve selected
                </Button>
                <Button size="sm" variant="outline" disabled={selected.size === 0 || transition.isPending}
                  onClick={bulkReject}>
                  <X className="h-3 w-3 mr-1" /> Reject selected
                </Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                      onCheckedChange={(c) => toggleAll(!!c)}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-32">Updated</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-40 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(item.id)}
                        onCheckedChange={(c) => toggleOne(item.id, !!c)}
                        aria-label={`Select ${item.title}`}
                      />
                    </TableCell>
                    <TableCell className="min-w-0">
                      <Link to={`/partner/research/${item.id}`} className="font-medium text-primary hover:underline">
                        {item.title}
                      </Link>
                      {item.summary && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{item.summary}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-muted-foreground">
                        {item.primary_axis && <span>{item.primary_axis}</span>}
                        {item.conviction && <span>· Conviction {item.conviction}/5</span>}
                        {item.portfolio_level && <span>· P{item.portfolio_level}</span>}
                        {item.ai_assisted && <Badge variant="outline" className="text-[10px] h-4">AI</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(item.updated_at), "dd/MM/yyyy")}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColor[item.status] || ""}>{item.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost"
                          disabled={transition.isPending || item.status === "approved"}
                          onClick={() => applyStatus([item.id], "approved")}
                          title="Approve">
                          <Check className="h-3 w-3 text-emerald-600" />
                        </Button>
                        <Button size="sm" variant="ghost"
                          disabled={transition.isPending || item.status === "rejected"}
                          onClick={() => applyStatus([item.id], "rejected")}
                          title="Reject">
                          <X className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
